import { useState } from 'react'
import { useAuth } from '../lib/auth'
import { useAccountClosure } from '../lib/account-closure'
import { ApiError } from '../lib/errors'
import { SupportEmail } from './legal/legal-page'

/** "3 January 2027 at 03:41" — the date the account is deleted. */
export function formatClosureDate(iso: string): string {
  return new Date(iso).toLocaleString('en', { dateStyle: 'long', timeStyle: 'short' })
}

/**
 * Cancels the pending deletion. Once the job has started removing things the
 * server answers 409 CLOSURE_IN_PROGRESS: that is a dead end only support can
 * help with, so it says so rather than offering a retry.
 */
export function CancelDeletionButton({ className }: { className?: string }): JSX.Element {
  const { cancel } = useAccountClosure()
  const [busy, setBusy] = useState(false)
  const [failure, setFailure] = useState<'in-progress' | 'other' | null>(null)

  async function onCancel(): Promise<void> {
    setBusy(true)
    setFailure(null)
    try {
      await cancel()
    } catch (e) {
      setFailure(e instanceof ApiError && e.code === 'CLOSURE_IN_PROGRESS' ? 'in-progress' : 'other')
      setBusy(false)
    }
  }

  return (
    <>
      <button className={className ?? 'account-closure__cancel'} onClick={() => void onCancel()} disabled={busy}>
        {busy ? 'Cancelling…' : 'Cancel deletion'}
      </button>
      {failure && (
        <p className="account-closure__error" role="status">
          {failure === 'in-progress'
            ? 'The deletion has already started and can no longer be cancelled. '
            : 'Could not cancel the deletion. Please try again. '}
          Questions? Write to <SupportEmail />.
        </p>
      )}
    </>
  )
}

/**
 * On every page, signed in, while an account closure is pending. Not
 * dismissible: the one thing a customer must not miss is that their photographs
 * are scheduled for deletion.
 */
export default function AccountClosureBanner(): JSX.Element | null {
  const { isAuthenticated } = useAuth()
  const { closure } = useAccountClosure()
  if (!isAuthenticated || !closure) return null

  const date = formatClosureDate(closure.dueAt)
  return (
    <div className="account-closure" role="alert">
      <p className="account-closure__text">
        {closure.phase === 'closing'
          ? `You asked to delete your account. It will be deleted on ${date}.`
          : `Your account will be deleted on ${date}. Until then you can only cancel.`}
      </p>
      <CancelDeletionButton />
    </div>
  )
}

/**
 * The last day, and while the job runs: the app is closed to everything but
 * cancelling, matching the server's ACCOUNT_LOCKED.
 */
export function AccountLocked(): JSX.Element | null {
  const { closure } = useAccountClosure()
  if (!closure) return null
  return (
    <section className="account-locked">
      <h1 className="account-locked__title">Your account is about to be deleted</h1>
      <p>
        It will be deleted on {formatClosureDate(closure.dueAt)}. Until then you can only cancel the deletion.
      </p>
      {closure.phase === 'deleting' && (
        <p>
          The deletion has started and may no longer be possible to cancel. Questions? Write to <SupportEmail />.
        </p>
      )}
      <CancelDeletionButton />
    </section>
  )
}
